import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "./LegalDraftPage.css";

const documents = {
  us: {
    title: "U.S. Terms of Use",
    file: "terms-us.md",
    route: "/terms",
    description: "The proposed U.S. baseline terms, including the 18+ eligibility rule."
  },
  california: {
    title: "California Supplement",
    files: ["terms-us.md", "terms-california.md"],
    route: "/terms/california",
    description: "State-specific terms intended to accompany the U.S. baseline."
  },
  colorado: {
    title: "Colorado Supplement",
    files: ["terms-us.md", "terms-colorado.md"],
    route: "/terms/colorado",
    description: "Colorado-specific contract and document-selection provisions."
  },
  safety: {
    title: "Colorado Safety Policy",
    file: "safety-colorado.md",
    route: "/safety/colorado",
    description: "A separate policy with operational disclosures awaiting verification."
  },
  newyork: {
    title: "New York Supplement",
    files: ["terms-us.md", "terms-new-york.md"],
    route: "/terms/new-york",
    description: "New York-specific contract and fraud-notice provisions."
  }
};

function inlineText(text, key) {
  return text.split(/(\*\*.*?\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={key + "-" + index}>{part.slice(2, -2)}</strong>
      : part
  );
}

function renderMarkdown(markdown) {
  const lines = markdown.split(/\r?\n/);
  const nodes = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      const value = paragraph.join(" ").trim();
      const identity = value.match(/^\*\*Who runs BublyUp:(.*?)\*\*\s+\*\*Contact:(.*?)\*\*$/);
      if (identity) {
        nodes.push(
          <aside className="legal-company-callout" key={"company-" + nodes.length}>
            <p><strong>Who runs BublyUp:{identity[1]}</strong></p>
            <p><strong>Contact:{identity[2]}</strong></p>
          </aside>
        );
      } else {
        nodes.push(<p key={"p-" + nodes.length}>{inlineText(value, "p-" + nodes.length)}</p>);
      }
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      nodes.push(
        <ul key={"ul-" + nodes.length}>
          {list.map((item, index) => <li key={index}>{inlineText(item, "li-" + nodes.length + "-" + index)}</li>)}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((line, index) => {
    const value = line.trim();
    if (/^\*\*Effective date:\*\*\s*\[TO BE COMPLETED\]$/i.test(value)) return;
    if (!value) {
      flushParagraph();
      flushList();
      return;
    }
    const heading = value.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const Tag = heading[1].length === 1 ? "h1" : heading[1].length === 2 ? "h2" : "h3";
      nodes.push(<Tag key={"h-" + index}>{inlineText(heading[2], "h-" + index)}</Tag>);
      return;
    }
    if (value.startsWith("- ")) {
      flushParagraph();
      list.push(value.slice(2));
      return;
    }
    flushList();
    paragraph.push(value);
  });
  flushParagraph();
  flushList();
  return nodes;
}

export default function LegalDraftPage({ documentId: requestedDocumentId }) {
  const { documentId: routeDocumentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const documentId = requestedDocumentId || routeDocumentId;
  const legalReturnTo = location.state?.legalReturnTo || "/";
  const returnState = { legalReturnTo };
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(Boolean(documentId));
  const [error, setError] = useState(false);
  const selected = documents[documentId];

  useEffect(() => {
    if (!selected) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all((selected.files || [selected.file]).map((file) =>
      fetch("/legal-preview/" + file).then((response) => {
        if (!response.ok) throw new Error("Draft could not be loaded");
        return response.text();
      })
    ))
      .then((documentsText) => { if (active) setMarkdown(documentsText.join("\n\n")); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selected]);

  useEffect(() => {
    if (documentId !== "us") return undefined;
    let active = true;
    fetch("/api/me", { credentials: "include" })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!active) return;
        const profile = data?.user?.legalLocation?.legal_profile;
        const routeByProfile = {
          "US-CA": documents.california.route,
          "US-CO": documents.colorado.route,
          "US-NY": documents.newyork.route
        };
        if (routeByProfile[profile]) {
          navigate(routeByProfile[profile], { replace: true, state: location.state });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [documentId, location.state, navigate]);

  return (
    <main className="legal-draft-page">
      <div className="legal-draft-shell">
        {selected ? (
          <>
            {loading ? <p>Loading draft…</p> : error ? (
              <p role="alert">This draft could not be loaded. Please try again later.</p>
            ) : (
              <>
                <article className="legal-draft-content">{renderMarkdown(markdown)}</article>
                <div className="legal-draft-bottom-actions">
                  <Link className="legal-draft-close" to={legalReturnTo}>Close and return to the previous page</Link>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <p className="legal-draft-eyebrow">BublyUp · United States</p>
            <h1>Legal document preview</h1>
            <p className="legal-draft-intro">The U.S. basics, plus a few extra details for members in specific states. State pages work alongside the U.S. Terms.</p>
            <div className="legal-draft-cards">
              {Object.entries(documents).map(([id, document]) => (
                <Link className="legal-draft-card" to={document.route} state={returnState} key={id}>
                  <span>{document.title}</span>
                  <small>{document.description}</small>
                  <b>Open draft →</b>
                </Link>
              ))}
            </div>
            <p className="legal-draft-disclaimer">These pages are still being reviewed. We will fill in the open details and check that every safety and privacy statement matches how BublyUp works before putting any terms into effect.</p>
            <div className="legal-draft-bottom-actions">
              <Link className="legal-draft-close" to={legalReturnTo}>Close and return to the previous page</Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
